import React, { useState, useEffect, useCallback } from 'react'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Button } from '@material-ui/core'
import ReplyRoundedIcon from '@material-ui/icons/ReplyRounded'

import { mapNumToBool } from 'commons/boolean'
import { useUserProfile } from 'commons/useUserProfile'

import Loader from '../Loader'
import Creator from '../Creator'
import ReplyBox from '../ReplyBox/ReplyBox'
import useCommentBox from './useCommentBox'
import CommentContainer from '../CommentContainer'

const CommentBox = (props) => {
  const { id, data, onFetchCommentList } = props
  const {
    text,
    creator,
    replies,
    editable,
    updatedAt,
    createdAt,
    id: commentId,
    status: commentStatus,
  } = data

  const profile = useUserProfile()
  const { formatMessage } = useIntl()

  const {
    state: {
      isUpdatingComment,
      isUpdateCommentSuccess,
      isSubmitingReply,
      isSubmitReplySuccess,
      isDeleteCommentSuccess,
    },
    reset,
    onUpdateComment,
    onDeleteComment,
    onSubmittedReply,
  } = useCommentBox(id, commentId, profile.id, onFetchCommentList())

  const [isReply, setIsReply] = useState(false)
  const [isEditComment, setIsEditComment] = useState(false)
  const [isCreatingReply, setIsCreatingReply] = useState(false)
  const [isDeletedComment, setIsDeletedComment] = useState(false)

  const formik = useFormik({
    initialValues: {
      reply: '',
    },
  })

  const handleReset = useCallback(async () => {
    await setIsEditComment(false)
    reset()
  }, [reset])

  useEffect(() => {
    return () => {
      formik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      isUpdateCommentSuccess ||
      isSubmitReplySuccess ||
      isDeleteCommentSuccess
    )
      setTimeout(() => {
        handleReset()
      }, 500)
  }, [
    handleReset,
    isDeleteCommentSuccess,
    isSubmitReplySuccess,
    isUpdateCommentSuccess,
  ])

  useEffect(() => {
    if (isSubmitReplySuccess) setIsCreatingReply(true)
  }, [isSubmitReplySuccess])

  useEffect(() => {
    setIsCreatingReply(false)
  }, [data.replies])

  return (
    <div className="py-10 space-y-5">
      {!isEditComment && (
        <Creator
          creator={creator}
          editable={editable}
          createdAt={createdAt}
          updatedAt={updatedAt}
          commentStatus={commentStatus}
          onEdit={() => setIsEditComment(true)}
          disabled={isDeletedComment || isEditComment}
          onDelete={() => {
            setIsDeletedComment(true)
            onDeleteComment()
          }}
        />
      )}

      <CommentContainer
        text={text}
        submitText={formatMessage({
          id: 'action.save',
        })}
        isInput={isEditComment}
        commentStatus={commentStatus}
        isLoading={isUpdatingComment}
        placeholder={`${formatMessage({ id: 'timeline.detail.message.edit.comment' })}....`}
        isSuccess={isUpdateCommentSuccess}
        onClose={() => setIsEditComment(false)}
        onSubmit={async (value) => {
          await onUpdateComment(value)
        }}
      />

      {mapNumToBool(commentStatus) ? (
        <div className="space-y-3">
          <Button
            className="p-0 justify-start text-secondary text-14 font-400 normal-case disabled:text-primary-300 hover:bg-transparent"
            onClick={() => setIsReply(true)}
            disabled={isReply}
          >
            <ReplyRoundedIcon
              fontSize="small"
              style={{
                transform: 'rotate(180deg)',
                color: isReply ? '#5D7290' : '#00B2AD',
              }}
            />
            {formatMessage({ id: 'timeline.detail.message.reply' })}
          </Button>
          {isReply ? (
            <CommentContainer
              text={''}
              isInput={isReply}
              submitText={formatMessage({
                id: 'timeline.detail.message.reply',
              })}
              isLoading={isSubmitingReply}
              commentStatus={commentStatus}
              placeholder={`${formatMessage({ id: 'timeline.detail.message.add.reply' })}....`}
              isSuccess={isSubmitReplySuccess}
              onClose={() => setIsReply(false)}
              onSubmit={async (value) => {
                await onSubmittedReply(value)
              }}
            />
          ) : null}
        </div>
      ) : null}

      {isCreatingReply ? (
        <div className="flex">
          <ReplyRoundedIcon
            fontSize="small"
            style={{ transform: 'rotate(180deg)', color: '#5D7290' }}
          />
          <Loader className="w-full pl-5" />
        </div>
      ) : null}

      {mapNumToBool(commentStatus) && replies.length > 0
        ? replies.map((replie) => (
            <div key={replie.id}>
              <ReplyBox
                id={id}
                data={replie}
                onFetchCommentList={onFetchCommentList}
              />
            </div>
          ))
        : null}
    </div>
  )
}

const creator = {
  id: PropTypes.number,
  firstName: PropTypes.string,
  lastname: PropTypes.string,
  avatar: PropTypes.string,
  avatarCharacters: PropTypes.string,
  deletedAt: PropTypes.string,
  status: PropTypes.number,
}

CommentBox.propTypes = {
  id: PropTypes.number,
  onFetchCommentList: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    creator: PropTypes.shape(creator),
    status: PropTypes.number,
    editable: PropTypes.number,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    replies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        text: PropTypes.string,
        creator: PropTypes.shape(creator),
        status: PropTypes.number,
        editable: PropTypes.number,
        createdAt: PropTypes.string,
        updatedAt: PropTypes.string,
      }),
    ),
  }),
}

export default CommentBox
